import React from 'react';
import {
    DefaultButton, DetailsList, DetailsListLayoutMode, IColumn, MessageBar, MessageBarType, Panel,
    PanelType, PrimaryButton, SearchBox, Selection, SelectionMode, Spinner, Stack, useTheme,
} from '@fluentui/react';
import { IContact } from '../../../models/Company';
import { useGetContactsQuery } from '../../../store/Api';
import { useAddStorefrontUsersMutation } from '../../../store/endpoints/StorefrontSettingsEndpoints';

/**
 * Finds contacts and grants them access to a storefront.
 *
 * Granting appends the storefront to the contact's additional-storefronts list, which is purely
 * additive — nobody added here loses whatever storefront they could already reach. The server
 * re-checks each contact and reports anyone skipped (already had access, or not found), and those
 * reasons are shown rather than swallowed: "3 added" when you selected 5 needs explaining.
 */
interface IStorefrontAddUsersPanelProps {
    isOpen: boolean;
    storefront: string;
    /** Called with how many were actually added, so the caller can confirm or stay quiet. */
    onDismiss: (added: number) => void;
}

const StorefrontAddUsersPanel: React.FC<IStorefrontAddUsersPanelProps> = (props) => {
    const theme = useTheme();
    const [search, setSearch] = React.useState('');
    // The term the query actually runs on. Each cache entry is now per-argument, so every keystroke
    // would otherwise be its own request; this keeps the typing responsive and the traffic sane.
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    React.useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search), 300);
        return () => window.clearTimeout(timer);
    }, [search]);
    const [saving, setSaving] = React.useState(false);
    const [skipped, setSkipped] = React.useState<{ contactId: string, reason: string }[]>([]);
    const selectedIds = React.useRef<string[]>([]);

    const contacts = useGetContactsQuery(
        { query: { query: debouncedSearch, page: 1, pageSize: 50 }, includeCompanyDetails: true },
        { skip: !props.isOpen },
    );
    const [addUsers] = useAddStorefrontUsersMutation();

    // Fluent's Selection lives outside React state, so it is built once and kept in a ref; the chosen
    // ids are mirrored into another ref and read on save.
    const selectionRef = React.useRef<Selection>();
    if (!selectionRef.current) {
        const created: Selection = new Selection({
            onSelectionChanged: () => {
                selectedIds.current = (created.getSelection() as IContact[]).map((c) => c.id ?? '').filter(Boolean);
            },
            getKey: (item) => (item as IContact).id ?? '',
        });
        selectionRef.current = created;
    }
    const selection = selectionRef.current;

    const onAdd = async () => {
        if (selectedIds.current.length === 0) return;
        setSaving(true);
        setSkipped([]);
        const result = await addUsers({ storefront: props.storefront, contactIds: selectedIds.current }).unwrap();
        setSaving(false);
        if (result.skipped?.length) {
            // Stay open so the reasons can be read; the grid behind has already been invalidated.
            setSkipped(result.skipped);
            return;
        }
        props.onDismiss(result.updated);
    };

    const columns: IColumn[] = [
        { key: 'name', name: 'Name', minWidth: 140, onRender: (c: IContact) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || '-' },
        { key: 'email', name: 'Email', minWidth: 200, onRender: (c: IContact) => c.emailAddress },
        { key: 'company', name: 'Company', minWidth: 140, onRender: (c: IContact) => c.companyName ?? '-' },
    ];

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={() => props.onDismiss(0)}
            headerText='Add Users'
            type={PanelType.medium}
            closeButtonAriaLabel='Close'
            // Same reason as StandardPanel: a pinned footer is transparent by default, so the whole
            // footer region (padding included) needs painting, not just the buttons.
            styles={{
                footer: {
                    backgroundColor: theme.palette.white,
                    borderTop: `1px solid ${theme.palette.neutralLight}`,
                },
            }}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton text='Add' onClick={onAdd} disabled={saving} />
                    <DefaultButton text='Cancel' onClick={() => props.onDismiss(0)} />
                </Stack>
            )}
            isFooterAtBottom
        >
            <p style={{ fontSize: 12, color: '#605e5c' }}>
                Search your contacts and pick who should be able to sign in to this storefront. This adds
                the storefront to them; it does not change any storefront they can already reach.
                Search matches whole words, so type a full name, address or company.
            </p>

            <SearchBox
                placeholder='Search by name, email or company'
                value={search}
                onChange={(_, v) => setSearch(v ?? '')}
                styles={{ root: { marginBottom: '1em' } }}
            />

            {skipped.length > 0 && (
                <MessageBar messageBarType={MessageBarType.warning} onDismiss={() => setSkipped([])} styles={{ root: { marginBottom: '1em' } }}>
                    <div>Some contacts were skipped:</div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                        {skipped.map((s) => <li key={s.contactId}>{s.reason}</li>)}
                    </ul>
                </MessageBar>
            )}

            {(contacts.isLoading || contacts.isFetching || saving) && <Spinner label={saving ? 'Adding...' : 'Loading...'} />}

            <DetailsList
                items={contacts.data?.items ?? []}
                columns={columns}
                selection={selection}
                selectionMode={SelectionMode.multiple}
                layoutMode={DetailsListLayoutMode.justified}
                setKey='addStorefrontUsers'
                selectionPreservedOnEmptyClick
            />
        </Panel>
    );
};

export default StorefrontAddUsersPanel;
